import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, GitCompare, Plus, Gamepad2, Clapperboard, Sun, Coins, Backpack } from "lucide-react";
import { Button } from "../components/ui/button";
import { getProducts, formatEUR } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { useLang } from "../i18n/LanguageContext";
import { toast } from "sonner";

const ROWS = [
  ["price", (p) => formatEUR(p.price_eur)],
  ["brightness", (p) => `${p.specs.lumens_ansi} ANSI`],
  ["resolution", (p) => p.specs.resolution],
  ["throw", (p, t) => `${p.specs.throw_ratio}:1 (${t(`throwTypes.${p.specs.throw_type}`)})`],
  ["contrast", (p) => p.specs.contrast],
  ["smart", (p) => p.specs.smart_os],
  ["speakers", (p) => p.specs.speakers],
  ["noise", (p) => `${p.specs.noise_db} dB`],
  ["connectivity", (p) => (p.specs.connectivity || []).join(", ")],
  ["weight", (p) => `${p.specs.weight_kg} kg`],
];
const ROW_LABEL_KEYS = { price: "specL.price", brightness: "specL.brightness", resolution: "specL.resolution", throw: "common.throw", contrast: "specL.contrast", smart: "specL.smartOs", speakers: "specL.speakers", noise: "specL.noise", connectivity: "specL.connectivity", weight: "specL.weight" };

export default function Compare() {
  const { t, lang } = useLang();
  const { compare, toggleCompare, addToCart } = useStore();
  const [all, setAll] = useState([]);
  useEffect(() => { getProducts({ lang }).then((d) => setAll(d.products)).catch(() => {}); }, [lang]);
  const items = compare.map((slug) => all.find((p) => p.slug === slug)).filter(Boolean);

  const verdict = (fn, icon) => {
    if (!items.length) return null;
    const best = [...items].sort(fn)[0];
    return { best, icon };
  };
  const verdicts = [
    { key: "bestGaming", ...(verdict((a, b) => (a.specs.input_lag_ms || 99) - (b.specs.input_lag_ms || 99), Gamepad2) || {}) },
    { key: "bestCinema", ...(verdict((a, b) => b.specs.contrast.localeCompare(a.specs.contrast), Clapperboard) || {}) },
    { key: "brightest", ...(verdict((a, b) => b.specs.lumens_ansi - a.specs.lumens_ansi, Sun) || {}) },
    { key: "bestValue", ...(verdict((a, b) => a.price_eur - b.price_eur, Coins) || {}) },
    { key: "mostPortable", ...(verdict((a, b) => a.specs.weight_kg - b.specs.weight_kg, Backpack) || {}) },
  ];

  return (
    <div className="pt-24 pb-16 container-lumeo">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl hairline shadow-glow-cyan mx-auto"><GitCompare className="h-7 w-7 text-cyanx" /></span>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl">{t("compare.title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("compare.subtitle")}</p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl card-ceramic">
          <p className="text-muted-foreground">{t("compare.empty")}</p>
          <Button asChild className="mt-4 btn-glow border-0"><Link to="/shop">{t("compare.addProjectors")}</Link></Button>
        </div>
      ) : (
        <>
          {/* verdicts */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {verdicts.map((v) => v.best && (
              <div key={v.key} data-testid={`compare-verdict-${v.key}`} className="relative rounded-xl card-ceramic p-3 text-center overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(86,227,255,0.5), transparent)" }} />
                <div className="h-12 w-12 mx-auto rounded-lg bg-black/40 hairline overflow-hidden">
                  <img src={v.best.image} alt="" className="h-full w-full object-contain p-1" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyanx/80 mt-2 flex items-center justify-center gap-1">
                  {v.icon && <v.icon className="h-3.5 w-3.5" />}{t(`compare.${v.key}`)}
                </p>
                <p className="text-sm font-medium mt-1">{v.best.name.replace("Magcubic ", "")}</p>
                <p className="font-mono text-xs text-muted-foreground mt-0.5">{formatEUR(v.best.price_eur)}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-2xl card-ceramic">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr>
                  <th className="p-4 text-left w-32 sticky left-0 bg-[#0f121a]"></th>
                  {items.map((p) => (
                    <th key={p.slug} className="p-4 min-w-[180px]">
                      <div className="relative">
                        <button onClick={() => toggleCompare(p.slug)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full glass inline-flex items-center justify-center"><X className="h-3 w-3" /></button>
                        <Link to={`/product/${p.slug}`}><div className="h-24 rounded-lg bg-black/40 overflow-hidden mb-2"><img src={p.image} alt={p.name} className="h-full w-full object-contain p-2" /></div>
                        <p className="font-display text-sm leading-tight">{p.name}</p></Link>
                        <Button onClick={() => { addToCart({ slug: p.slug, name: p.name, price_eur: p.price_eur, image: p.image, kind: "product" }); toast.success(t("common.added")); }} size="sm" className="btn-glow border-0 mt-2 w-full"><Plus className="h-3 w-3 mr-1" />{formatEUR(p.price_eur)}</Button>
                      </div>
                    </th>
                  ))}
                  {Array.from({ length: 3 - items.length }).map((_, i) => (
                    <th key={i} className="p-4 min-w-[180px]"><Link to="/shop" className="flex flex-col items-center justify-center h-40 rounded-lg hairline text-muted-foreground hover:text-cyanx"><Plus className="h-6 w-6" /><span className="text-xs mt-1">{t("compare.addSlot")}</span></Link></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([key, fn]) => (
                  <tr key={key} className="border-t border-border">
                    <td className="p-4 text-xs font-mono uppercase tracking-wider text-muted-foreground sticky left-0 bg-[#0f121a]">{t(ROW_LABEL_KEYS[key])}</td>
                    {items.map((p) => <td key={p.slug} className="p-4 text-sm">{fn(p, t)}</td>)}
                    {Array.from({ length: 3 - items.length }).map((_, i) => <td key={i} className="p-4">–</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
